/**
 * Grid band colours, ported from TMS `src/lib/gridColors.ts`.
 *
 * Eight values: two themes × editable/read-only × light/dark band. The grid
 * paints alternating bands so a wide row stays readable across the screen, and
 * a different tint for cells the user may actually type into.
 *
 * **This module no longer paints anything, and holds no colours.** The
 * CSS-variable injector that used to live here (`applyGridColors`, `<style id="numtable-grid-colors">`)
 * was generalised into `src/shared/theme/themeColors.ts`, which covers the whole
 * site rather than the grid alone. Exactly one `<style>` may compete for these
 * custom properties, so the old one is gone rather than deprecated. What is
 * left is the legacy *shape*: the twelve keys a stored `grid_colors` row still
 * has, which `upgradeGridColors` reads and `cellColors`/`SheetPreview` still
 * describe cells with. The named palettes moved too, as `THEME_PRESETS`, and
 * cover the whole site rather than six grid roles.
 */

export interface GridColors {
  light_edit_pale: string;
  light_edit_deep: string;
  light_readonly_pale: string;
  light_readonly_deep: string;
  light_header: string;
  light_chip: string;
  dark_edit_pale: string;
  dark_edit_deep: string;
  dark_readonly_pale: string;
  dark_readonly_deep: string;
  dark_header: string;
  dark_chip: string;
}

export const GRID_COLOR_KEYS: readonly (keyof GridColors)[] = [
  'light_edit_pale',
  'light_edit_deep',
  'light_readonly_pale',
  'light_readonly_deep',
  'light_header',
  'light_chip',
  'dark_edit_pale',
  'dark_edit_deep',
  'dark_readonly_pale',
  'dark_readonly_deep',
  'dark_header',
  'dark_chip',
];

export function isHexColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

export function isGridColors(value: unknown): value is GridColors {
  return Boolean(
    value &&
      typeof value === 'object' &&
      GRID_COLOR_KEYS.every((key) => {
        const candidate = (value as Record<string, unknown>)[key];
        return typeof candidate === 'string' && isHexColor(candidate);
      }),
  );
}
